import { AssetTypesEnum } from '@types';

export type BlockingCheckGroupId =
  | 'identification'
  | 'asset-details'
  | 'registration-certificate'
  | 'bank-statement';

export type BlockingCheck = {
  id: string;
  groupId: BlockingCheckGroupId;
  groupLabel: string;
  name: string;
  conditionLabel: string;
  checkType: string;
};

export type BlockingCheckGroup = {
  id: BlockingCheckGroupId;
  label: string;
  checks: BlockingCheck[];
};

export const INVALID_CHECK_TYPES_BY_ASSET: Record<AssetTypesEnum, string[]> = {
  [AssetTypesEnum.CAR]: [],
  [AssetTypesEnum.MOTOR_CYCLE]: [],
  [AssetTypesEnum.CAMPERVAN]: [],
  [AssetTypesEnum.TRAILER]: [],
  [AssetTypesEnum.EQUIPMENT]: [],
  [AssetTypesEnum.MARINE]: [
    'asset-details-stolen-check',
    'asset-details-written-off-check',
    'asset-details-nevdis-registration-status',
    'asset-details-vin-plate-match',
    'registration-certificate-expiry-date-match',
    'registration-certificate-document-authenticity'
  ],
  [AssetTypesEnum.MARINE_WITH_TRAILER]: [
    'asset-details-stolen-check',
    'asset-details-written-off-check',
    'asset-details-nevdis-registration-status',
    'asset-details-vin-plate-match',
    'registration-certificate-expiry-date-match',
    'registration-certificate-document-authenticity'
  ]
};

const GROUP_LABELS: Record<BlockingCheckGroupId, string> = {
  identification: 'Identification',
  'asset-details': 'Asset details',
  'registration-certificate': 'Registration certificate',
  'bank-statement': 'Bank statement'
};

const GROUP_ORDER: BlockingCheckGroupId[] = [
  'identification',
  'asset-details',
  'registration-certificate',
  'bank-statement'
];

export const BLOCKING_CHECKS: BlockingCheck[] = [
  {
    id: 'ID_EXPIRY',
    groupId: 'identification',
    groupLabel: GROUP_LABELS.identification,
    name: 'ID Expiry',
    conditionLabel: 'Expired',
    checkType: 'identification-id-expiry'
  },
  {
    id: 'ID_FACE_MATCH',
    groupId: 'identification',
    groupLabel: GROUP_LABELS.identification,
    name: 'Face Match',
    conditionLabel: 'Low Confidence',
    checkType: 'identification-face-match'
  },
  {
    id: 'ID_LIVENESS',
    groupId: 'identification',
    groupLabel: GROUP_LABELS.identification,
    name: 'Liveness Check',
    conditionLabel: 'Low Confidence',
    checkType: 'identification-liveness-check'
  },
  {
    id: 'ID_AUTHENTICITY',
    groupId: 'identification',
    groupLabel: GROUP_LABELS.identification,
    name: 'Document Authenticity',
    conditionLabel: 'Low Confidence',
    checkType: 'identification-document-authenticity'
  },
  {
    id: 'ASSET_PPSR_ENCUMBRANCE',
    groupId: 'asset-details',
    groupLabel: GROUP_LABELS['asset-details'],
    name: 'Financial Interest (PPSR)',
    conditionLabel: 'Registered Interest Found',
    checkType: 'asset-details-financial-interest'
  },
  {
    id: 'ASSET_STOLEN',
    groupId: 'asset-details',
    groupLabel: GROUP_LABELS['asset-details'],
    name: 'Stolen Check',
    conditionLabel: 'Stolen',
    checkType: 'asset-details-stolen-check'
  },
  {
    id: 'ASSET_WRITTEN_OFF',
    groupId: 'asset-details',
    groupLabel: GROUP_LABELS['asset-details'],
    name: 'Written-off Check',
    conditionLabel: 'Written-off',
    checkType: 'asset-details-written-off-check'
  },
  {
    id: 'ASSET_VIN_REGO_MATCH',
    groupId: 'asset-details',
    groupLabel: GROUP_LABELS['asset-details'],
    name: 'VIN & Number Plate Match',
    conditionLabel: 'Mismatch',
    checkType: 'asset-details-vin-plate-match'
  },
  {
    id: 'ASSET_REGISTRATION',
    groupId: 'asset-details',
    groupLabel: GROUP_LABELS['asset-details'],
    name: 'NEVDIS Registration Status',
    conditionLabel: 'Expired',
    checkType: 'asset-details-nevdis-registration-status'
  },
  {
    id: 'ASSET_PHOTOS_AUTHENTICITY',
    groupId: 'asset-details',
    groupLabel: GROUP_LABELS['asset-details'],
    name: 'AI Photo Check',
    conditionLabel: 'Suspected Screen Photo',
    checkType: 'asset-details-ai-photo-check'
  },
  {
    id: 'ASSET_PHOTOS_QUALITY',
    groupId: 'asset-details',
    groupLabel: GROUP_LABELS['asset-details'],
    name: 'AI Photo Check',
    conditionLabel: 'Poor Quality (Blurred, Cropped, Dark)',
    checkType: 'asset-details-ai-photo-check'
  },
  {
    id: 'REGO_CERTIFICATE_EXPIRY_MATCH',
    groupId: 'registration-certificate',
    groupLabel: GROUP_LABELS['registration-certificate'],
    name: 'Expiry Date Match',
    conditionLabel: 'Mismatch with NEVDIS',
    checkType: 'registration-certificate-expiry-date-match'
  },
  {
    id: 'REGO_CERTIFICATE_AUTHENTICITY',
    groupId: 'registration-certificate',
    groupLabel: GROUP_LABELS['registration-certificate'],
    name: 'Document Authenticity',
    conditionLabel: 'Low Confidence',
    checkType: 'registration-certificate-document-authenticity'
  },
  {
    id: 'BANK_STATEMENT_AUTHENTICITY',
    groupId: 'bank-statement',
    groupLabel: GROUP_LABELS['bank-statement'],
    name: 'Document Authenticity',
    conditionLabel: 'Low Confidence',
    checkType: 'bank-statement-document-authenticity'
  },
  {
    id: 'BANK_STATEMENT_ADDRESS_MATCH',
    groupId: 'bank-statement',
    groupLabel: GROUP_LABELS['bank-statement'],
    name: 'Address Match',
    conditionLabel: 'Mismatch with Registration Certificate',
    checkType: 'bank-statement-address-match'
  }
];

export const getBlockingChecksForAsset = (
  assetType: AssetTypesEnum
): BlockingCheck[] => {
  const invalidCheckTypes = INVALID_CHECK_TYPES_BY_ASSET[assetType] || [];
  return BLOCKING_CHECKS.filter(
    (check) => !invalidCheckTypes.includes(check.checkType)
  );
};

export const getBlockingCheckGroups = (
  assetType: AssetTypesEnum
): BlockingCheckGroup[] => {
  const checks = getBlockingChecksForAsset(assetType);

  return GROUP_ORDER.flatMap((groupId) => {
    const groupChecks = checks.filter((check) => check.groupId === groupId);
    if (groupChecks.length === 0) {
      return [];
    }

    return [
      {
        id: groupId,
        label: GROUP_LABELS[groupId],
        checks: groupChecks
      }
    ];
  });
};

export const getBlockingCheckIdsFromRuleTypes = (
  ruleTypes?: string[] | null
): string[] => {
  if (!ruleTypes?.length) {
    return [];
  }

  const knownIds = new Set(BLOCKING_CHECKS.map((check) => check.id));
  return Array.from(new Set(ruleTypes.filter((ruleType) => knownIds.has(ruleType))));
};

export const getRuleTypesFromCheckIds = (checkIds: string[]): string[] =>
  Array.from(
    new Set(
      checkIds.filter((id) => BLOCKING_CHECKS.some((check) => check.id === id))
    )
  );

export const filterCheckIdsForAsset = (
  checkIds: string[],
  assetType: AssetTypesEnum
): string[] => {
  const availableIds = new Set(
    getBlockingChecksForAsset(assetType).map((check) => check.id)
  );
  return checkIds.filter((id) => availableIds.has(id));
};

export const matchesBlockingCheckSearch = (
  check: BlockingCheck,
  searchTerm: string
) => {
  const normalized = searchTerm.trim().toLowerCase();
  if (!normalized) {
    return true;
  }

  return (
    check.name.toLowerCase().includes(normalized) ||
    check.conditionLabel.toLowerCase().includes(normalized)
  );
};

export const formatBlockingCheckFooter = (count: number) =>
  count > 0
    ? `${count} ${count === 1 ? 'blocking check' : 'blocking checks'}`
    : 'no blocking checks';
